import type { Cell, MatchGroup, TileData } from "./types";

/**
 * Pure match-detection utilities for the board grid.
 *
 * Important rules:
 *   - Matching is always by `personId`, never `photoId` or `photoUrl`.
 *   - Two same-person runs that touch but are different photos count as one run.
 *   - When a horizontal and a vertical match share cells (cross / L / T shape)
 *     the shared cells must appear in exactly one match group (deduplicated).
 */
export class MatchDetector {
  /** Returns the (row, col) -> TileData grid from the board, accepting undefined. */
  static asGrid(
    board: (TileData | null)[][],
  ): (TileData | null)[][] {
    return board;
  }

  /**
   * Detect every contiguous same-personId run (horizontal and vertical)
   * of length >= minLength. Shared cells across H and V runs are deduplicated:
   * each cell appears in exactly one returned group.
   */
  static detect(
    board: (TileData | null)[][],
    minLength = 3,
  ): MatchGroup[] {
    const rows = board.length;
    const cols = rows > 0 ? board[0].length : 0;

    type Stride = { cells: Cell[]; personId: string };
    const horizontals: Stride[] = [];
    const verticals: Stride[] = [];

    // Horizontal scan
    for (let r = 0; r < rows; r++) {
      let c = 0;
      while (c < cols) {
        const tile = board[r][c];
        if (!tile) {
          c++;
          continue;
        }
        let k = c + 1;
        while (k < cols && board[r][k] && board[r][k]!.personId === tile.personId) k++;
        const len = k - c;
        if (len >= minLength) {
          const cells: Cell[] = [];
          for (let j = c; j < k; j++) cells.push({ row: r, col: j });
          horizontals.push({ cells, personId: tile.personId });
        }
        c = k;
      }
    }

    // Vertical scan
    for (let co = 0; co < cols; co++) {
      let r = 0;
      while (r < rows) {
        const tile = board[r][co];
        if (!tile) {
          r++;
          continue;
        }
        let k = r + 1;
        while (k < rows && board[k][co] && board[k][co]!.personId === tile.personId) k++;
        const len = k - r;
        if (len >= minLength) {
          const cells: Cell[] = [];
          for (let j = r; j < k; j++) cells.push({ row: j, col: co });
          verticals.push({ cells, personId: tile.personId });
        }
        r = k;
      }
    }

    // Deduplicate cells that participate in both a horizontal and a vertical
    // match. Strategy: keep the vertical match intact, then add only the
    // horizontal cells that have not already been claimed.
    const claimed = new Set<string>();
    const key = (cell: Cell) => `${cell.row},${cell.col}`;
    const result: MatchGroup[] = [];

    for (const v of verticals) {
      for (const cell of v.cells) claimed.add(key(cell));
      result.push({ cells: v.cells.slice(), personId: v.personId });
    }
    for (const h of horizontals) {
      const unclaimed = h.cells.filter((c) => !claimed.has(key(c)));
      if (unclaimed.length >= minLength) {
        for (const cell of unclaimed) claimed.add(key(cell));
        result.push({ cells: unclaimed, personId: h.personId });
      } else if (unclaimed.length > 0) {
        // Bridge case: horizontal was split between two verticals (cross/T/L
        // topology). Merge the unclaimed pieces into the existing verticals
        // they touch so the player gets full credit for a "T" / cross shape.
        for (const cell of unclaimed) claimed.add(key(cell));
        // Attribute each unclaimed cell to the verticals it shares personId with.
        // Since same-person cells are equal, the verticals already have the
        // matching personId. For simplicity: extend the first vertical that
        // shares a personId with this run; if none, append as a new group.
        const target =
          result.find((g) => g.personId === h.personId) ??
          (() => {
            const g: MatchGroup = { cells: [], personId: h.personId };
            result.push(g);
            return g;
          })();
        for (const cell of unclaimed) target.cells.push(cell);
      }
    }

    return result;
  }

  /** Convenience: flat list of every cell that participates in any match. */
  static matchedCells(matches: MatchGroup[]): Set<string> {
    const set = new Set<string>();
    for (const g of matches) {
      for (const c of g.cells) set.add(`${c.row},${c.col}`);
    }
    return set;
  }
}
